import React, {FC, useEffect, useState, memo, FormEvent, useRef} from 'react';
import i18next from 'i18next';
import { useNavigate } from 'react-router';
import sanitizeHtml from "sanitize-html";
import { Button, Col } from 'react-bootstrap';
import BootstrapAvatar from "../myaccount/BootstrapAvatar";
import InfiniteScroll from 'react-infinite-scroll-component';
import ChatMessage from "./ChatMessage";
import {useChat} from "../../contexts/chat-context";

type Props = { therapy: any, therapist: any, isMobile: boolean };

const ChatMessageComponent: FC<Props> = ({ therapy, therapist, isMobile }) => {
    const navigate = useNavigate();
    const { sendMessage, resendMessage, loadMessages, loadMoreMessages, messages, hasMore } = useChat();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const firstLoadRef = useRef(true);

    const [formValue, setFormValue] = useState('');
    const [loadingMore, setLoadingMore] = useState(false);

    const getContainer = () => containerRef.current ?? document.getElementById('chatMessage');

    const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
      const el = getContainer();
      if (!el) return;

      el.scrollTo({ top: el.scrollHeight, behavior });
    }

  const loadMore = () => {
    void loadMoreMessages();
  }

  useEffect(() => {
    firstLoadRef.current = true;
    (async () => {
      await loadMessages(therapy._id);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToBottom('auto');
          firstLoadRef.current = false;
        });
      });
    })();
  }, [therapy._id]);

    const handleResendMessage = async (text: any) => {
        await resendMessage({ text: text, therapy });
        scrollToBottom('smooth');
    }
    const handleSend = async (e: React.KeyboardEvent | FormEvent) => {
        e.preventDefault();
        if (!formValue.trim()) return;

        await sendMessage({ text: formValue, therapy });
        setFormValue('');
        scrollToBottom('smooth');
    };

    const patientName = therapy ? `${therapy.patient.firstName} ${therapy.patient.lastName}` : '';

    if (!therapy) return null;

    return (
      <Col className="col-12 col-lg-8 col-md-7 col-xl-9 chat-message-container">

        <div className="py-2 px-4 border-bottom d-none d-lg-block">
          <div className="d-flex align-items-center py-1">
            <div className="position-relative">
              <BootstrapAvatar
                name={patientName}
                size={40}
                color="#707089"
                className="rounded-circle me-1"
                round={true}
              />
            </div>
            <div className="flex-grow-1 ps-4">
              <strong>{patientName}</strong>
              <div className="text-muted small"><em></em></div>
            </div>
            <div>
              <Button variant="link" onClick={() => navigate(`/therapien/${therapy.id}`)}>
                {i18next.t('chat.openPatientProfile')}
              </Button>
            </div>
          </div>
        </div>

        {messages.length > 0 ? (
          <div
            id="chatMessage"
            ref={containerRef}
            className="chat-messages px-4 pt-4 flex-column-reverse"
          >
            <InfiniteScroll
              className="chat-message-body p-4"
              dataLength={messages.length}
              next={loadMore}
              hasMore={hasMore}
              inverse={true}
              scrollableTarget="chatMessage"
              scrollThreshold="150px"
              loader={
                <Col md={12} className="loader text-center" key={0}>
                  <i className="fa fa-spinner fa-spin fa-2x" aria-hidden="true" />
                </Col>
              }
              style={{ display: 'flex', flexDirection: 'column-reverse' }}
            >
              {messages.map((message) => (
                <ChatMessage
                  key={`msg-${message.createdAt}-${message.senderId}`}
                  message={message}
                  therapist={therapist}
                  resendMessage={handleResendMessage}
                />
              ))}
              {isMobile ? (<><br /><br /></>) : (<br />)}
            </InfiniteScroll>
          </div>
        ) : (
          <div className="chat-messages p-4 text-center">{i18next.t('chat.noChatMessageFound')}</div>
        )}

        <div className="chat-input col-12 col-lg-8 col-md-7 col-xl-9">
          <form onSubmit={handleSend} id="sendMsgForm">
            <div className="flex-grow-0 py-3 px-4 border-top chat-textarea">
              <div className="input-group">
                <input
                  type="text"
                  className="message-wrapper"
                  placeholder={i18next.t('chat.typeMsgPlaceholder')}
                  value={formValue}
                  onChange={e => setFormValue(sanitizeHtml(e.target.value))}
                  onKeyUp={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' && !e.shiftKey && formValue) {
                      handleSend(e);
                    }
                  }}
                />
                <button type="submit" className="btn btn-primary float-end" disabled={!formValue}>
                  <i className="fas fa-paper-plane" aria-hidden="true" />
                </button>
              </div>
            </div>
          </form>
        </div>
      </Col>
    )
};

export default memo(ChatMessageComponent);
